import { css } from 'styled-components';
import Colr from 'colr';
import { buttonReset, getSmallButtonTokens, getMediumButtonTokens, getLargeButtonTokens, getPrimaryButtonTokens, getPrimaryButtonSelectedTokens, getPrimaryButtonDisabledTokens, getSecondaryButtonTokens, getSecondaryButtonSelectedTokens, getSecondaryButtonDisabledTokens, getTertiaryButtonTokens, getTertiaryButtonSelectedTokens, getTertiaryButtonDisabledTokens, getDestructiveButtonTokens, getDestructiveButtonSelectedTokens, getDestructiveButtonDisabledTokens } from '../shared';
import common, { spacing } from '../../common';
export const hoverColor = color => Colr.fromHex(color).darken(10).toHex();
export const activeColor = color => Colr.fromHex(color).darken(20).toHex();
export const disabledColor = (color, percent) => {
  const opacity = percent || 0.3;
  const rgb = Colr.fromHex(color).toRgbArray().toString();
  return `rgba(${rgb}, ${opacity})`;
};
const typeScale = {
  1: '3.375rem',
  2: '2.25rem',
  3: '1.5rem',
  4: '1.125rem',
  5: '1rem',
  6: '.875rem'
};
const lineHeights = [1, 1.25, 1.5];
const tracking = {
  normal: 'normal',
  tight: '-0.02em'
};
const fonts = {
  heading: '"FS Lola","Calibri", serif',
  copy: 'Arial, sans-serif'
};
const yellow = {
  20: '#fffae1',
  40: '#fff0a7',
  60: '#ffe566',
  80: '#ffd400',
  100: '#f2ba00'
};
const blue = {
  20: '#c2ddef',
  40: '#3698d8',
  60: '#0076c2',
  80: '#004a79',
  100: '#042c49'
};
const darkBlue = '#002a49';
const brandGradient = {
  normal: `linear-gradient(90deg,#007fc4 0,#007fc4 50%,${darkBlue})`,
  hover: 'linear-gradient(90deg,#0072b0 0,#0072b0 50%,#001e35)'
};
const colors = {
  brand: {
    base: '#005b88',
    light: blue[60],
    dark: darkBlue
  },
  accent: yellow[80],
  shades: {
    black: '#000000',
    darkest: '#535353',
    darker: '#666666',
    dark: '#999999',
    light: '#cccccc',
    lighter: '#dddddd',
    lightest: '#f2f2f2',
    nearWhite: '#f9f8f6',
    white: '#ffffff'
  },
  focus: blue[60],
  success: '#458340',
  error: '#cc0033',
  info: blue[60],
  product: {
    hospital: yellow[80],
    extras: blue[60],
    combined: blue[80],
    highlight: {
      backgroundColor: yellow[80],
      textColor: blue[100]
    }
  },
  // CUSTOM FALL-BACKS FOR NIB-SPECIFIC COLOURS
  trueGreen: darkBlue,
  trueGreen80: blue[80],
  trueGreen60: blue[60],
  trueGreen40: blue[40],
  trueGreen20: blue[20],
  brightGreen: yellow[100],
  brightGreen80: yellow[80],
  brightGreen60: yellow[60],
  brightGreen40: yellow[40],
  brightGreen20: yellow[20],
  sunsetPink: '#dddddd',
  sunsetPink80: '#eeeeee',
  sunsetPink60: '#eeeeee',
  sunsetPink40: '#f9f9f9',
  sunsetPink20: '#f9f9f9',
  sageGreen: '#dddddd',
  sageGreen80: '#eeeeee',
  sageGreen60: '#eeeeee',
  sageGreen40: '#f9f9f9',
  sageGreen20: '#f9f9f9',
  warmWhite: '#eeeeee',
  warmWhite80: '#eeeeee',
  warmWhite60: '#f9f9f9',
  warmWhite40: '#f9f9f9',
  warmWhite20: '#f9f9f9'
};
const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '3px',
  outlineOffset: '0.125em'
};
const baseButtonStyles = css`
  ${buttonReset};

  font-family: ${fonts.copy};
  font-weight: 600;
  letter-spacing: ${tracking.normal};
  transition-duration: 0.2s;
  transition-timing-function: ease;
  transition-property: background, color;
  border: none;
  border-radius: 4px;
`;
const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles}
      ${getPrimaryButtonTokens({
      bg: {
        default: blue[60],
        hover: blue[100],
        active: blue[100],
        disabled: blue[60]
      },
      fg: {
        default: colors.shades.white,
        hover: colors.shades.white,
        active: colors.shades.white,
        disabled: '#c8c8c8'
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent',
        width: '0'
      }
    })};

      background: ${brandGradient.normal};
      box-shadow: inset 0 -2px 0 0 #003d5e;

      &:hover:not([disabled]) {
        background: ${brandGradient.hover};
      }
    `,
    selected: css`
      ${getPrimaryButtonSelectedTokens({
      bg: blue[80],
      fg: colors.shades.white,
      border: 'transparent'
    })}
    `,
    disabled: css`
      ${getPrimaryButtonDisabledTokens({
      bg: blue[60],
      fg: '#c8c8c8',
      border: 'transparent'
    })}
      cursor: not-allowed;
    `
  },
  secondary: {
    normal: css`
      ${baseButtonStyles};

      ${getSecondaryButtonTokens({
      bg: {
        default: '#f1f1f1',
        hover: '#e8e6e3',
        active: '#f1f1f1',
        disabled: '#f1f1f1'
      },
      fg: {
        default: '#005b88',
        hover: '#005b88',
        active: blue[80],
        disabled: '#c8c8c8'
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent',
        width: '0'
      }
    })};

      box-shadow: inset 0 -2px 0 0 #bbb;

      &:hover:not([disabled]) {
        box-shadow: inset 0 -2px 0 0 #bebebe;
      }
    `,
    selected: css`
      ${getSecondaryButtonSelectedTokens({
      bg: '#f1f1f1',
      fg: blue[80],
      border: 'transparent'
    })}
    `,
    disabled: css`
      ${getSecondaryButtonDisabledTokens({
      bg: '#f1f1f1',
      fg: '#c8c8c8',
      border: 'transparent'
    })}
      cursor: not-allowed;
    `
  },
  tertiary: {
    normal: css`
      ${baseButtonStyles}

      ${getTertiaryButtonTokens({
      bg: {
        default: colors.shades.white,
        hover: colors.shades.white,
        active: colors.shades.white,
        disabled: colors.shades.white
      },
      fg: {
        default: blue[60],
        hover: blue[80],
        active: blue[80],
        disabled: '#c8c8c8'
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent',
        width: '0'
      }
    })};
    `,
    selected: css`
      ${getTertiaryButtonSelectedTokens({
      bg: colors.shades.white,
      fg: blue[80],
      border: 'transparent'
    })};
    `,
    disabled: css`
      ${getTertiaryButtonDisabledTokens({
      bg: colors.shades.white,
      fg: '#c8c8c8',
      border: 'transparent'
    })};

      cursor: not-allowed;
    `
  },
  destructive: {
    normal: css`
      ${baseButtonStyles}

      ${getDestructiveButtonTokens({
      bg: {
        default: '#f1f1f1',
        hover: '#e8e6e3',
        active: '#f1f1f1',
        disabled: '#f1f1f1'
      },
      fg: {
        default: colors.error,
        hover: hoverColor(colors.error),
        active: activeColor(colors.error),
        disabled: '#c8c8c8'
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent',
        width: '0'
      }
    })};

      box-shadow: inset 0 -2px 0 0 #bbb;

      &:hover:not([disabled]) {
        box-shadow: inset 0 -2px 0 0 #bebebe;
      }
    `,
    selected: css`
      ${getDestructiveButtonSelectedTokens({
      bg: '#f1f1f1',
      fg: activeColor(colors.error),
      border: 'transparent'
    })};
    `,
    disabled: css`
      ${getDestructiveButtonDisabledTokens({
      bg: '#f1f1f1',
      fg: '#c8c8c8',
      border: 'transparent'
    })};

      cursor: not-allowed;
    `
  },
  small: css`
    ${getSmallButtonTokens({
    fontFamily: fonts.copy,
    fontSize: typeScale[6],
    fontWeight: 600,
    lineHeight: lineHeights[0],
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[3],
    paddingInline: spacing[3],
    gap: 'unset'
  })}
  `,
  medium: css`
    ${getMediumButtonTokens({
    fontFamily: fonts.copy,
    fontSize: typeScale[5],
    fontWeight: 600,
    lineHeight: lineHeights[2],
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[3],
    paddingInline: spacing[5],
    gap: 'unset'
  })}
  `,
  large: css`
    ${getLargeButtonTokens({
    fontFamily: fonts.copy,
    fontSize: typeScale[4],
    fontWeight: 600,
    lineHeight: lineHeights[1],
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[4],
    paddingInline: spacing[5],
    gap: 'none'
  })}
  `
};
const baseFormValues = {
  borderColor: colors.shades.dark,
  borderColorValid: colors.success,
  borderColorInvalid: colors.error,
  selectedToggleColor: blue[60],
  helpTextColor: colors.shades.darker,
  helpTextSize: typeScale[6],
  labelColor: colors.shades.darkest,
  labelSize: typeScale[5],
  errorMessageTextColor: colors.error,
  infoMessageTextColor: colors.info
};
const aanz = {
  ...common,
  typeScale,
  fonts,
  colors,
  focus,
  id: 'aanz',
  mode: {
    form: 'light' //default
  },
  headings: {
    fontFamily: fonts.copy,
    color: colors.brand.dark,
    lineHeight: lineHeights[1],
    h1: {
      fontFamily: fonts.heading,
      fontSize: typeScale[1],
      fontWeight: 800,
      lineHeight: lineHeights[0],
      tracking: tracking.tight,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.heading,
      fontSize: typeScale[2],
      lineHeight: lineHeights[0],
      fontWeight: 700,
      tracking: tracking.tight,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.heading,
      fontSize: typeScale[3],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.tight,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.heading,
      fontSize: typeScale[4],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.copy,
      fontSize: typeScale[5],
      lineHeight: lineHeights[2],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h6: {
      fontFamily: fonts.copy,
      fontSize: typeScale[5],
      lineHeight: lineHeights[2],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    }
  },
  copy: {
    color: colors.shades.darkest,
    fontFamily: fonts.copy,
    fontSize: typeScale[5],
    fontWeight: 400,
    lineHeight: lineHeights[2],
    smallFontSize: typeScale[6],
    largeFontSize: typeScale[4]
  },
  link: {
    loudColor: blue[60],
    darkColor: colors.shades.darkest,
    lightColor: colors.shades.white,
    destructiveColor: colors.error
  },
  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: buttonStyles.primary,
    secondary: buttonStyles.secondary,
    tertiary: buttonStyles.tertiary,
    destructive: buttonStyles.destructive
  },
  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `0 4px 8px 0 ${colors.shades.lighter}`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  },
  smallFooter: {
    backgroundColor: yellow[80],
    textColor: colors.shades.black,
    linkColor: colors.shades.black
  },
  panel: {
    loud: {
      backgroundColor: colors.brand.base,
      textColor: colors.shades.white
    },
    muted: {
      backgroundColor: colors.shades.lighter,
      textColor: colors.shades.darkest
    },
    light: {
      backgroundColor: colors.shades.lightest,
      textColor: colors.shades.darkest
    },
    dark: {
      backgroundColor: colors.shades.darkest,
      textColor: colors.shades.white
    },
    white: {
      backgroundColor: colors.shades.white,
      textColor: colors.shades.darkest
    }
  }
};
export default aanz;